import { createClient, RpcError, type CallOptions } from '@flowology/client';
import { reportConnection } from './connection/connection.ts';
import { setCompanyMode } from './shared/mirror.ts';

let csrf: string | null = null;
export const setCsrf = (value: string | null) => {
  csrf = value;
};
// When a call finds the session gone, the app asks to sign in again over the screen (QA-251).
let lost: () => void = () => undefined;
export const onSessionLost = (fn: () => void) => {
  lost = fn;
};
// Past a required two-factor's deadline the api holds every call (QA-089): the app holds its places too.
let twoFactorHeld: () => void = () => undefined;
export const onTwoFactorHeld = (fn: () => void) => {
  twoFactorHeld = fn;
};
// A call answered not found (QA-565): an open customer record hears it, to follow a merge.
const notFound = new Set<(action: string, input: unknown) => void>();
export const onNotFound = (fn: (action: string, input: unknown) => void) => {
  notFound.add(fn);
  return () => {
    notFound.delete(fn);
  };
};
// A call refused for want of a permission (QA-682): what the person holds may have changed since
// sign-in (the owner took one back), so the app reads it again and the nav and pages follow.
const forbidden = new Set<() => void>();
export const onForbidden = (fn: () => void) => {
  forbidden.add(fn);
  return () => {
    forbidden.delete(fn);
  };
};
// A lost connection is ridden out: calls retry with the same key for two minutes (QA-058).
const rpc = createClient({
  csrf: () => csrf,
  onUnauthenticated: () => lost(),
  onTwoFactorRequired: () => twoFactorHeld(),
  connection: { retryForMs: 120_000, onChange: reportConnection },
});
export const client: typeof rpc = {
  ...rpc,
  async call<T>(action: string, input?: unknown, opts?: CallOptions) {
    try {
      return await rpc.call<T>(action, input, opts);
    } catch (e) {
      if (e instanceof RpcError && e.code === 'not_found') for (const fn of notFound) fn(action, input);
      if (e instanceof RpcError && e.code === 'forbidden') for (const fn of forbidden) fn();
      // refused because the company mirrors ServiceTitan: every screen learns it, not just this one
      if (e instanceof RpcError && (e.detail as { reason?: unknown } | null)?.reason === 'mirrored')
        setCompanyMode('mirror');
      throw e;
    }
  },
};
